/* ==========================================================================
   Build a Bundle — Chumi
   Persistent-tray bundle builder. Vanilla web component, no external libraries.
   ========================================================================== */
(function () {
  'use strict';

  var STORAGE_PREFIX = 'bab:';

  function uid() {
    return 'bndl-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
  }

  class BuildABundle extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      var configEl = this.querySelector('[data-bab-config]');
      this.config = configEl ? JSON.parse(configEl.textContent) : {};

      // Bundle-size state is driven by user-selected size buttons in the tray.
      // Each tier carries its full perk payload as data attributes:
      //   total      - boxes the customer selects (2/3/4/5)
      //   packs      - free packs added on top
      //   boxes      - special boxes added free
      //   tshirt     - whether a free t-shirt is added
      //   free-shipping - always true here
      // Initialize from the button marked :checked (default 2 Boxes).
      var defaultSizeBtn = this.querySelector('[data-bab-size]:checked')
        || this.querySelector('[data-bab-size]');
      if (defaultSizeBtn) {
        this.totalSlots    = parseInt(defaultSizeBtn.dataset.babSizeTotal, 10) || 2;
        this.packCount     = parseInt(defaultSizeBtn.dataset.babSizePacks, 10) || 0;
        this.boxCount      = parseInt(defaultSizeBtn.dataset.babSizeBoxes, 10) || 0;
        this.tshirtOn      = defaultSizeBtn.dataset.babSizeTshirt === 'true';
        this.freeShipping  = defaultSizeBtn.dataset.babSizeFreeShipping === 'true';
      } else {
        this.totalSlots    = this.config.totalSlots || 2;
        this.packCount     = 0;
        this.boxCount      = 0;
        this.tshirtOn      = false;
        this.freeShipping  = false;
      }
      // Bundle is valid only when ALL selectable box slots are filled.
      this.minBundle = this.totalSlots;

      // Gift products (pack + special box + t-shirt) the customer never picks —
      // they're appended as free line items and zeroed by 100%-off automatic
      // discounts.
      this.giftPackVariantId = this.config.giftPackVariantId || null;
      this.giftBoxVariantId = this.config.giftBoxVariantId || null;
      this.giftTshirtVariantId = this.config.giftTshirtVariantId || null;

      this.storageKey = STORAGE_PREFIX + (this.config.sectionId || 'default');

      // Selling plans from the subscription app (empty = buy-once only).
      this.sellingPlans = this.config.sellingPlans || [];
      this.hasSubscription = this.sellingPlans.length > 0;

      this.cart = document.querySelector('cart-drawer, cart-page');
      this.tray = this.querySelector('[data-bab-tray]');
      this.slotMap = [];

      // Mobile-only layout reorganization. Runs on init; desktop keeps the
      // original sidebar layout untouched.
      this.applyMobileLayout();

      this.catalog = this.buildCatalog();
      this.state = this.loadState();

      this.bindEvents();
      this.bindFooterAwareLift();
      this.render();
    }

    /* Push the fixed tray up by the visible-footer height so it always sits
       right above the page footer instead of overlaying it. The tray's
       transform is driven by a CSS custom property (--bab-footer-lift)
       updated on scroll/resize. Desktop is unaffected because the tray
       isn't fixed there. */
    bindFooterAwareLift() {
      var footer = document.querySelector('footer-group, .footer-group, .site-footer, footer');
      if (!footer || !this.tray) return;
      var self = this;
      var ticking = false;

      var update = function () {
        ticking = false;
        if (!window.matchMedia('(max-width: 574px)').matches) {
          self.tray.style.setProperty('--bab-footer-lift', '0px');
          return;
        }
        var rect = footer.getBoundingClientRect();
        var viewportH = window.innerHeight;
        var overlap = Math.max(0, viewportH - rect.top);
        self.tray.style.setProperty('--bab-footer-lift', overlap + 'px');
      };

      var onScroll = function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', update);
      update();
    }

    /* --- Catalog: read product data from the rendered grid cards --------- */
    buildCatalog() {
      var catalog = {};
      this.querySelectorAll('[data-bab-card]').forEach(function (card) {
        var id = card.dataset.babVariantId;
        if (!id) return;
        catalog[id] = {
          variantId: id,
          productId: card.dataset.babProductId,
          handle: card.dataset.babHandle,
          title: card.dataset.babTitle,
          line: card.dataset.babLine,
          price: parseInt(card.dataset.babPrice, 10) || 0,
          image: card.dataset.babImage || '',
          available: card.dataset.babAvailable === 'true'
        };
      });
      return catalog;
    }

    /* --- State persistence (sessionStorage) ----------------------------- */
    defaultState() {
      return {
        items: {},
        purchaseType: 'onetime',
        sellingPlanId: this.sellingPlans[0] ? this.sellingPlans[0].id : null
      };
    }

    planById(id) {
      for (var i = 0; i < this.sellingPlans.length; i++) {
        if (String(this.sellingPlans[i].id) === String(id)) return this.sellingPlans[i];
      }
      return null;
    }

    /* Discount % for the current selection (0 unless Subscribe is active). */
    subscribePct() {
      if (this.state.purchaseType !== 'subscribe') return 0;
      var plan = this.planById(this.state.sellingPlanId);
      return plan ? (plan.discountPct || 0) : 0;
    }

    loadState() {
      var fallback = this.defaultState();
      try {
        var raw = sessionStorage.getItem(this.storageKey);
        if (!raw) return fallback;
        var saved = JSON.parse(raw);
        var state = this.defaultState();
        Object.keys(saved.items || {}).forEach(function (vid) {
          if (this.catalog[vid] && this.catalog[vid].available) {
            state.items[vid] = saved.items[vid];
          }
        }, this);
        if (this.hasSubscription && saved.purchaseType === 'subscribe') {
          state.purchaseType = 'subscribe';
        }
        if (saved.sellingPlanId != null && this.planById(saved.sellingPlanId)) {
          state.sellingPlanId = saved.sellingPlanId;
        }
        return state;
      } catch (e) {
        return fallback;
      }
    }

    saveState() {
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(this.state));
      } catch (e) { /* storage unavailable — non-fatal */ }
    }

    clearState() {
      this.state = this.defaultState();
      try { sessionStorage.removeItem(this.storageKey); } catch (e) {}
    }

    /* --- Derived values -------------------------------------------------- */
    filledCount() {
      var total = 0;
      var items = this.state.items;
      for (var k in items) { if (items.hasOwnProperty(k)) total += items[k]; }
      return total;
    }

    isFull() {
      return this.filledCount() >= this.totalSlots;
    }

    canCheckout() {
      return this.filledCount() >= this.minBundle;
    }

    /* Flat list of unit prices across all picked cartons. */
    unitPrices() {
      var prices = [];
      var items = this.state.items;
      for (var vid in items) {
        if (!items.hasOwnProperty(vid) || !this.catalog[vid]) continue;
        for (var i = 0; i < items[vid]; i++) prices.push(this.catalog[vid].price);
      }
      return prices;
    }

    computeTotals() {
      var prices = this.unitPrices();
      var pct = this.subscribePct();
      // Per-unit price after the subscribe discount (if any).
      var unitPrices = prices.map(function (p) {
        return pct > 0 ? Math.round(p * (1 - pct / 100)) : p;
      });
      // Boxes are charged at full price — the perks (pack, box, t-shirt) are
      // free add-ons fulfilled by automatic discounts at checkout, not a
      // discount on the boxes themselves.
      var fullSubtotal = prices.reduce(function (a, b) { return a + b; }, 0);
      var subtotal = unitPrices.reduce(function (a, b) { return a + b; }, 0);
      return {
        filled: prices.length,
        struck: fullSubtotal,
        discounted: subtotal,
        full: prices.length >= this.totalSlots
      };
    }

    /* --- Item mutations -------------------------------------------------- */
    addItem(variantId) {
      if (!this.catalog[variantId] || !this.catalog[variantId].available) return;

      // Auto-upgrade to the next larger bundle size if we're at capacity.
      // (2 → 3 → 4 → 5 Boxes). Prevents impossible "5/4 Selected" states.
      if (this.isFull()) {
        var nextSizeRadio = this.findNextSize();
        if (!nextSizeRadio) return; // already at the largest size
        this.applySize(nextSizeRadio);
      }

      this.state.items[variantId] = (this.state.items[variantId] || 0) + 1;
      this.commit();
    }

    /* Return the [data-bab-size] radio for the smallest available size that
       is strictly larger than the current totalSlots. Null if none exists. */
    findNextSize() {
      var current = this.totalSlots;
      var radios = this.querySelectorAll('[data-bab-size]');
      var best = null;
      var bestTotal = Infinity;
      radios.forEach(function (radio) {
        var t = parseInt(radio.dataset.babSizeTotal, 10);
        if (t > current && t < bestTotal) {
          best = radio;
          bestTotal = t;
        }
      });
      return best;
    }

    decItem(variantId) {
      if (!this.state.items[variantId]) return;
      this.state.items[variantId] -= 1;
      if (this.state.items[variantId] <= 0) delete this.state.items[variantId];
      this.commit();
    }

    commit() {
      this.saveState();
      this.render();
    }

    /* --- Money ----------------------------------------------------------- */
    money(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        var fmt = (window.settings && window.settings.moneyFormat) || this.config.moneyFormat;
        return window.Shopify.formatMoney(cents, fmt);
      }
      return '$' + (cents / 100).toFixed(2);
    }

    /* --- Mobile layout: reorganize the tray for the small-screen flow ----
       1. Move the tray to the LAST child of <body> so position: fixed isn't
          broken by any transformed ancestor inside <main>.
       2. Move Step 1 ("Pick your bundle size" + 4/5/8 buttons) ABOVE the
          product grid inside the section. The size selector is the first
          decision a customer makes, so it should sit above the products,
          not be buried in the bottom tray.
       3. Replace the tray's "Save with bundle" toggle label with the live
          progress bar + "0/N Selected" counter, so the always-visible bar
          shows real-time progress instead of a generic label.

       Desktop (≥575px) keeps the original sidebar layout untouched.
    */
    applyMobileLayout() {
      if (!window.matchMedia('(max-width: 574px)').matches) return;

      // IMPORTANT: grab references to elements we want to relocate BEFORE
      // moving the tray. Once the tray is detached from <build-a-bundle>
      // and re-parented to <body>, any descendants of the tray (including
      // .bab-step--size) are no longer reachable via this.querySelector,
      // which only searches <build-a-bundle>'s subtree.
      var sizeStep = this.querySelector('.bab-step--size');
      var grid = this.querySelector('[data-bab-grid]');
      var progress = this.tray && this.tray.querySelector('[data-bab-progress-mobile]');
      var toggle = this.tray && this.tray.querySelector('.bab-tray__toggle');
      var caret = toggle && toggle.querySelector('.bab-tray__toggle-caret');

      // 1. Tray → last child of body
      if (this.tray && document.body && this.tray !== document.body.lastElementChild) {
        document.body.appendChild(this.tray);
      }

      // 2. Step 1 (size buttons) → above the product grid
      if (sizeStep && grid && grid.parentNode && sizeStep.parentNode !== grid.parentNode) {
        grid.parentNode.insertBefore(sizeStep, grid);
      }

      // 3. Progress bar → into the toggle button, in front of the caret
      if (progress && toggle && progress.parentNode !== toggle) {
        if (caret) {
          toggle.insertBefore(progress, caret);
        } else {
          toggle.appendChild(progress);
        }
      }
    }

    /* --- Event wiring ---------------------------------------------------- */
    bindEvents() {
      var clickHandler = this.onClick.bind(this);
      var changeHandler = this.onChange.bind(this);
      this.addEventListener('click', clickHandler);
      this.addEventListener('change', changeHandler);
      // Tray was relocated to <body> in connectedCallback, so its events no
      // longer bubble up into <build-a-bundle>. Bind directly on the tray
      // so size buttons, ATC, close, etc. still work.
      if (this.tray) {
        this.tray.addEventListener('click', clickHandler);
        this.tray.addEventListener('change', changeHandler);
      }
    }

    onClick(evt) {
      var t = evt.target;
      var card = t.closest('[data-bab-card]');

      if ((t.closest('[data-bab-add]') || t.closest('[data-bab-inc]')) && card) {
        this.addItem(card.dataset.babVariantId);
        return;
      }
      if (t.closest('[data-bab-dec]') && card) {
        this.decItem(card.dataset.babVariantId);
        return;
      }
      if (t.closest('[data-bab-pick-for-me]')) {
        this.pickForMe();
        return;
      }
      if (t.closest('[data-bab-filter]')) {
        this.applyFilter(t.closest('[data-bab-filter]').dataset.babFilter);
        return;
      }
      if (t.closest('[data-bab-tile-remove]')) {
        var tile = t.closest('[data-bab-tile]');
        var vid = this.slotMap[parseInt(tile.dataset.babTileIndex, 10)];
        if (vid) this.decItem(vid);
        return;
      }
      // Size selector is now <input type="radio"> — handled by onChange below.
      if (t.closest('[data-bab-tray-close]')) {
        // X button: minimize the tray to just the title bar + progress bar.
        this.tray.removeAttribute('data-bab-tray-open');
        this.tray.setAttribute('data-bab-tray-minimized', '');
        var toggleBtn = this.tray.querySelector('[data-bab-tray-toggle]');
        if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
        return;
      }
      if (t.closest('[data-bab-tray-toggle]')) {
        // Tapping the title bar: if we were minimized, un-minimize first so
        // the user lands back in the default (size + ATC visible) view.
        if (this.tray.hasAttribute('data-bab-tray-minimized')) {
          this.tray.removeAttribute('data-bab-tray-minimized');
          this.scrollGridIntoView();
          return;
        }
        this.toggleTray();
        return;
      }
      if (t.closest('[data-bab-add-to-cart]')) {
        this.addToCart();
        return;
      }
    }

    onChange(evt) {
      var t = evt.target;
      if (t.matches('[data-bab-filter-select]')) {
        this.applyFilter(t.value);
        return;
      }
      if (t.matches('[data-bab-size]')) {
        this.applySize(t);
        return;
      }
      if (t.matches('[data-bab-purchase-radio]')) {
        this.state.purchaseType = t.dataset.babPurchaseType === 'subscribe' ? 'subscribe' : 'onetime';
        if (t.dataset.babPlanId) this.state.sellingPlanId = t.dataset.babPlanId;
        this.commit();
        return;
      }
      if (t.matches('[data-bab-frequency]')) {
        this.state.sellingPlanId = t.value;
        this.commit();
        return;
      }
    }

    /* --- Bundle-size selection ------------------------------------------- */
    applySize(btn) {
      var newTotal    = parseInt(btn.dataset.babSizeTotal, 10) || this.totalSlots;
      var newPacks    = parseInt(btn.dataset.babSizePacks, 10) || 0;
      var newBoxes    = parseInt(btn.dataset.babSizeBoxes, 10) || 0;
      var newTshirt   = btn.dataset.babSizeTshirt === 'true';

      var unchanged = newTotal === this.totalSlots
        && newPacks === this.packCount
        && newBoxes === this.boxCount
        && newTshirt === this.tshirtOn;
      if (unchanged) {
        // Already on this size — just sync the visual state and return.
        this.syncSizeButtons(btn);
        return;
      }

      this.totalSlots    = newTotal;
      this.minBundle     = newTotal;
      this.packCount     = newPacks;
      this.boxCount      = newBoxes;
      this.tshirtOn      = newTshirt;
      this.freeShipping  = btn.dataset.babSizeFreeShipping === 'true';

      this.syncSizeButtons(btn);

      // If the user picked a smaller size, trim the last-picked items so the
      // bundle doesn't overflow the new slot count.
      var filled = this.filledCount();
      if (filled > this.totalSlots) {
        for (var i = filled - 1; i >= this.totalSlots; i--) {
          var vid = this.slotMap[i];
          if (vid && this.state.items[vid] > 0) {
            this.state.items[vid]--;
            if (this.state.items[vid] === 0) delete this.state.items[vid];
          }
        }
      }

      this.commit();
    }

    syncSizeButtons(activeBtn) {
      // activeBtn is the <input type="radio">. Toggle the `is-selected`
      // class on its parent <label> so the CSS .is-selected fallback works
      // in browsers without :has() support.
      this.querySelectorAll('[data-bab-size]').forEach(function (input) {
        var on = input === activeBtn;
        input.checked = on;
        var label = input.closest('.bab-size');
        if (label) label.classList.toggle('is-selected', on);
      });
    }

    toggleTray() {
      if (!this.tray) return;
      var open = this.tray.toggleAttribute('data-bab-tray-open');
      var toggle = this.tray.querySelector('[data-bab-tray-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) this.scrollGridIntoView();
    }

    /* On mobile, align the 3-product grid to the top of the viewport so the
       customer sees: products on top, expanded tray (size buttons + sticky
       ATC) on the bottom. The flavor tiles + frequency radios live below the
       fold and are reached by scrolling inside the tray's collapsible. */
    scrollGridIntoView() {
      if (!window.matchMedia('(max-width: 574px)').matches) return;
      var grid = this.querySelector('[data-bab-grid]');
      if (!grid) return;
      requestAnimationFrame(function () {
        var top = grid.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({ top: top - 8, behavior: 'smooth' });
      });
    }

    /* --- "Pick for me" --------------------------------------------------- */
    pickForMe() {
      var handles = this.config.presetHandles || [];
      if (!handles.length) return;
      var byHandle = {};
      for (var vid in this.catalog) {
        if (this.catalog[vid].available) byHandle[this.catalog[vid].handle] = vid;
      }
      var picks = [];
      handles.forEach(function (h) { if (byHandle[h]) picks.push(byHandle[h]); });
      if (!picks.length) return;

      this.state.items = {};
      var i = 0;
      while (this.filledCount() < this.totalSlots) {
        var v = picks[i % picks.length];
        this.state.items[v] = (this.state.items[v] || 0) + 1;
        i++;
      }
      this.commit();
    }

    /* --- Filtering ------------------------------------------------------- */
    applyFilter(value) {
      var grid = this.querySelector('[data-bab-grid]');
      if (grid) {
        grid.querySelectorAll('[data-bab-card]').forEach(function (card) {
          var show = value === 'all' || card.dataset.babLine === value;
          card.classList.toggle('is-hidden', !show);
        });
      }
      this.querySelectorAll('[data-bab-filter]').forEach(function (pill) {
        var active = pill.dataset.babFilter === value;
        pill.classList.toggle('is-active', active);
        pill.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      this.querySelectorAll('[data-bab-filter-select]').forEach(function (sel) {
        sel.value = value;
      });
    }

    /* --- Perks summary for the current tier ----------------------------- */
    perkSummary() {
      var parts = [];
      if (this.freeShipping) parts.push('free shipping');
      if (this.packCount > 0) parts.push('a free pack');
      if (this.boxCount > 0) parts.push('a free box');
      if (this.tshirtOn) parts.push('a free t-shirt');
      return parts.join(', ');
    }

    /* --- Status message (milestones) ------------------------------------ */
    statusMessage(filled) {
      if (filled >= this.totalSlots) {
        var perks = this.perkSummary();
        return perks ? 'Bundle complete — includes ' + perks + '.' : 'Bundle complete!';
      }
      var need = this.totalSlots - filled;
      return 'Add ' + (need === 1 ? 'one' : need) + ' more box' + (need === 1 ? '' : 'es') + ' to complete your bundle.';
    }

    /* --- Rendering ------------------------------------------------------- */
    render() {
      this.renderGridCards();
      this.renderTray();
    }

    renderGridCards() {
      var self = this;
      var full = this.isFull();
      this.querySelectorAll('[data-bab-card]').forEach(function (card) {
        var vid = card.dataset.babVariantId;
        var qty = self.state.items[vid] || 0;
        var add = card.querySelector('[data-bab-add]');
        var stepper = card.querySelector('[data-bab-stepper]');
        var qtyEl = card.querySelector('[data-bab-qty]');
        var inc = card.querySelector('[data-bab-inc]');
        if (!add || !stepper) return;

        if (qty > 0) {
          add.hidden = true;
          stepper.hidden = false;
          if (qtyEl) qtyEl.textContent = qty;
        } else {
          add.hidden = false;
          stepper.hidden = true;
        }
        add.disabled = full;
        add.setAttribute('aria-disabled', full ? 'true' : 'false');
        if (inc) {
          inc.disabled = full;
          inc.setAttribute('aria-disabled', full ? 'true' : 'false');
        }
        card.classList.toggle('is-selected', qty > 0);
      });
    }

    /* Sync the per-tier perks list (free shipping / pack / box / t-shirt).
       Driven purely by the selected size, not by fill progress. */
    updatePerks() {
      if (!this.tray) return;
      var perks = this.tray.querySelector('[data-bab-perks]');
      if (!perks) return;
      var toggle = function (name, on) {
        var el = perks.querySelector('[data-bab-perk="' + name + '"]');
        if (!el) return;
        el.classList.toggle('is-hidden', !on);
        // Also set inline display so hiding works even if build-a-bundle.css
        // hasn't loaded yet (the `.bab-perk` display rule would otherwise
        // override the `is-hidden` class / [hidden] attribute).
        el.style.display = on ? '' : 'none';
      };
      toggle('shipping', this.freeShipping);
      toggle('pack', this.packCount > 0);
      toggle('box', this.boxCount > 0);
      toggle('tshirt', this.tshirtOn);
    }

    renderTray() {
      if (!this.tray) return;
      var totals = this.computeTotals();
      var filled = totals.filled;
      var q = this.tray.querySelector.bind(this.tray);

      /* Progress */
      var progress = q('.bab-progress');
      var progressText = q('[data-bab-progress-text]');
      var progressStatus = q('[data-bab-progress-status]');
      var progressFill = q('[data-bab-progress-fill]');
      if (progressText) progressText.textContent = filled + '/' + this.totalSlots + ' Selected';
      if (progressStatus) progressStatus.textContent = this.statusMessage(filled);
      if (progressFill) progressFill.style.width = (filled / this.totalSlots * 100) + '%';
      var allFilled = filled >= this.totalSlots;
      if (progress) {
        progress.classList.toggle('is-complete', allFilled);
        progress.classList.toggle('is-unlocked', allFilled);
      }

      /* Bundle item tiles */
      this.slotMap = [];
      var items = this.state.items;
      for (var vid in items) {
        if (!items.hasOwnProperty(vid)) continue;
        for (var i = 0; i < items[vid]; i++) this.slotMap.push(vid);
      }

      var self = this;

      // Sync the per-tier perks list (free shipping / pack / box / t-shirt).
      this.updatePerks();

      this.tray.querySelectorAll('[data-bab-tile]').forEach(function (tile, idx) {
        // Hide tiles beyond the selected size.
        var beyondSize = idx >= self.totalSlots;
        tile.classList.toggle('is-hidden', beyondSize);
        if (beyondSize) return;

        var picked = self.slotMap[idx];
        var img = tile.querySelector('.bab-tile__img');
        var num = tile.querySelector('.bab-tile__num');
        var title = tile.querySelector('[data-bab-tile-title]');
        var line = tile.querySelector('[data-bab-tile-line]');
        var remove = tile.querySelector('[data-bab-tile-remove]');
        var pos = 'Slot ' + (idx + 1) + ' of ' + self.totalSlots;

        if (picked && self.catalog[picked]) {
          var prod = self.catalog[picked];
          tile.classList.add('is-filled');
          tile.classList.remove('is-empty');
          if (img) {
            if (prod.image) { img.src = prod.image; img.hidden = false; }
            else { img.hidden = true; }
          }
          if (num) num.style.display = 'none';
          if (title) title.textContent = prod.title;
          if (line) { line.textContent = prod.line || ''; line.hidden = !prod.line; }
          if (remove) {
            remove.hidden = false;
            remove.disabled = false;
            remove.setAttribute('aria-disabled', 'false');
            remove.setAttribute('aria-label', pos + ': ' + prod.title + ', remove');
          }
        } else {
          tile.classList.remove('is-filled');
          tile.classList.add('is-empty');
          if (img) { img.hidden = true; img.removeAttribute('src'); }
          if (num) {
            num.style.display = '';
            num.textContent = idx + 1;
          }
          if (title) title.textContent = '';
          if (line) { line.textContent = ''; line.hidden = true; }
          if (remove) {
            remove.hidden = true;
            remove.disabled = true;
            remove.setAttribute('aria-disabled', 'true');
            remove.setAttribute('aria-label', pos + ': empty');
          }
        }
      });

      /* Purchase options — sync the radio that matches current state. */
      this.tray.querySelectorAll('[data-bab-purchase-radio]').forEach(function (radio) {
        var isMatch = false;
        if (radio.dataset.babPurchaseType === 'onetime') {
          isMatch = self.state.purchaseType === 'onetime';
        } else if (radio.dataset.babPurchaseType === 'subscribe') {
          isMatch = self.state.purchaseType === 'subscribe'
                 && String(radio.dataset.babPlanId) === String(self.state.sellingPlanId);
        }
        radio.checked = isMatch;
      });

      /* Add to cart */
      var canCheckout = this.canCheckout();
      var atc = q('[data-bab-add-to-cart]');
      var atcText = q('[data-bab-atc-text]');
      if (atc) {
        atc.disabled = !canCheckout;
        atc.setAttribute('aria-disabled', canCheckout ? 'false' : 'true');
      }
      if (atcText) {
        if (canCheckout) {
          var label = 'Add bundle to cart — ' + this.money(totals.discounted);
          if (totals.discounted < totals.struck) {
            label += ' <del>' + this.money(totals.struck) + '</del>';
          }
          atcText.innerHTML = label;
        } else {
          var need = this.minBundle - filled;
          atcText.textContent = 'Add ' + need + ' more box' + (need === 1 ? '' : 'es');
        }
      }
    }

    /* --- Build line items + add to cart --------------------------------- */
    buildItems() {
      var bundleId = uid();
      var items = [];
      var purchaseType = this.state.purchaseType;
      var planId = this.state.sellingPlanId;

      for (var vid in this.state.items) {
        if (!this.state.items.hasOwnProperty(vid)) continue;
        var item = {
          id: parseInt(vid, 10),
          quantity: this.state.items[vid],
          properties: { _bundle_id: bundleId, _purchase_type: purchaseType }
        };
        // Attach the Shopify selling plan so the line item becomes a real
        // subscription at checkout. The plan id comes from the subscription
        // app via product.selling_plan_groups (rendered into the config).
        if (purchaseType === 'subscribe' && planId) {
          item.selling_plan = parseInt(planId, 10);
        }
        items.push(item);
      }

      // Free pack — zeroed by a 100%-off automatic discount on that product.
      if (this.packCount > 0 && this.giftPackVariantId) {
        items.push({
          id: parseInt(this.giftPackVariantId, 10),
          quantity: this.packCount,
          properties: { _bundle_id: bundleId, _bundle_gift: 'pack' }
        });
      }

      // Special box — zeroed by a 100%-off automatic discount on that product.
      if (this.boxCount > 0 && this.giftBoxVariantId) {
        items.push({
          id: parseInt(this.giftBoxVariantId, 10),
          quantity: this.boxCount,
          properties: { _bundle_id: bundleId, _bundle_gift: 'box' }
        });
      }

      // Free t-shirt — zeroed by a 100%-off automatic discount on that product.
      if (this.tshirtOn && this.giftTshirtVariantId) {
        items.push({
          id: parseInt(this.giftTshirtVariantId, 10),
          quantity: 1,
          properties: { _bundle_id: bundleId, _bundle_gift: 'tshirt' }
        });
      }

      return items;
    }

    showError(msg) {
      var el = this.tray && this.tray.querySelector('[data-bab-error]');
      if (el) { el.textContent = msg; el.hidden = false; }
    }

    addToCart() {
      if (!this.canCheckout()) return;
      var atc = this.tray.querySelector('[data-bab-add-to-cart]');
      var errEl = this.tray.querySelector('[data-bab-error]');
      if (errEl) errEl.hidden = true;
      if (atc) { atc.disabled = true; atc.setAttribute('aria-disabled', 'true'); }

      var sectionNames = [];
      if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
        sectionNames = this.cart.getSectionsToRender().map(function (s) { return s.name; });
      }
      var body = { items: this.buildItems() };
      if (sectionNames.length) {
        body.sections = sectionNames;
        body.sections_url = window.location.pathname;
      }

      var self = this;
      fetch(window.routes.cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      })
        .then(function (res) { return res.json(); })
        .then(function (response) {
          if (response.status) {
            self.showError(response.description || response.message || 'Could not add bundle to cart.');
            if (atc) { atc.disabled = false; atc.setAttribute('aria-disabled', 'false'); }
            return;
          }
          self.onAddSuccess(response);
        })
        .catch(function () {
          self.showError('Something went wrong. Please try again.');
          if (atc) { atc.disabled = false; atc.setAttribute('aria-disabled', 'false'); }
        });
    }

    onAddSuccess(response) {
      try {
        if (this.cart && response.sections && typeof this.cart.getSectionsToRender === 'function') {
          this.cart.getSectionsToRender().forEach(function (section) {
            var html = new DOMParser().parseFromString(response.sections[section.name], 'text/html');
            var hostEl = document.getElementById(section.id);
            var currentSection = hostEl && hostEl.querySelector('#cart-drawer-form');
            if (!currentSection) return;

            var currentProducts = currentSection.querySelector('.cart-drawer__products');
            var currentFooter = currentSection.querySelector('.cart-drawer__footer');
            var parsedProducts = html.querySelector('.cart-drawer__products');
            var parsedFooter = html.querySelector('.cart-drawer__footer');

            if (currentFooter && parsedFooter && currentProducts && parsedProducts) {
              currentProducts.replaceChildren(
                document.createRange().createContextualFragment(parsedProducts.innerHTML)
              );
              currentFooter.replaceChildren(
                document.createRange().createContextualFragment(parsedFooter.innerHTML)
              );
            } else {
              var parsedForm = html.querySelector('#cart-drawer-form');
              if (parsedForm) currentSection.innerHTML = parsedForm.innerHTML;
            }
          });
          if (typeof this.cart.initCartActions === 'function') this.cart.initCartActions();
          this.cart.classList.remove('is-empty');
        }
      } catch (e) {
        window.location.href = window.routes.cartUrl;
        return;
      }

      fetch(window.routes.cartUrl + '.json')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          document.dispatchEvent(new CustomEvent('theme:cart:count:update', {
            detail: { count: cart.item_count }
          }));
        })
        .catch(function () {});

      this.clearState();
      this.applyFilter('all');
      this.render();

      if (this.cart && typeof this.cart.open === 'function') {
        this.cart.open(this);
      } else {
        window.location.href = window.routes.cartUrl;
      }
    }
  }

  if (!window.customElements.get('build-a-bundle')) {
    window.customElements.define('build-a-bundle', BuildABundle);
  }
})();
